import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Logo from "./Logo";
import { NAV_LINKS } from "@/data/site";
import { EASE } from "./ui-bits";

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass border-b border-line" : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" className="shrink-0" data-testid="nav-logo-link" aria-label="Androgen Health home">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `text-sm link-underline pb-0.5 transition-colors ${
                  isActive ? "text-teal" : "text-mute hover:text-bone"
                }`
              }
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/book"
            className="btn-primary !hidden sm:!inline-flex !py-2.5 !px-5 text-sm"
            data-testid="nav-book-assessment-button"
          >
            Book an assessment
          </Link>
          <button
            className="lg:hidden w-11 h-11 grid place-items-center rounded-full border border-line"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="nav-mobile-toggle"
          >
            <div className="space-y-1.5">
              <span className={`block w-5 h-px bg-bone transition-transform duration-300 ${open ? "translate-y-[3.5px] rotate-45" : ""}`} />
              <span className={`block w-5 h-px bg-bone transition-transform duration-300 ${open ? "-translate-y-[3.5px] -rotate-45" : ""}`} />
            </div>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden glass border-b border-line px-6 pt-2 pb-8"
            aria-label="Mobile"
            data-testid="nav-mobile-menu"
          >
            {NAV_LINKS.map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * i, duration: 0.45, ease: EASE }}
              >
                <Link
                  to={l.to}
                  className="block py-3.5 border-b border-line font-display text-3xl tracking-wide"
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {l.label}
                </Link>
              </motion.div>
            ))}
            <Link to="/book" className="btn-primary mt-6 w-full justify-center" data-testid="nav-mobile-cta">
              Book an assessment
            </Link>
            <Link to="/contact" className="btn-ghost mt-3 w-full justify-center" data-testid="nav-mobile-contact">
              Ask a question first
            </Link>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
